import React, { useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * CollapsibleSearch - A search input that starts as a magnifying glass icon button
 * and expands into a full search input when clicked.
 * 
 * Props:
 * - value: string - The current search value
 * - onChange: (value: string) => void - Called when search value changes
 * - placeholder: string - Placeholder text for the input
 * - className: string - Additional classes for the container
 * - inputClassName: string - Additional classes for the input
 * - "data-testid": string - Test ID for the component
 * - width: string - Width of the expanded input (default: "w-[300px]")
 */
export function CollapsibleSearch({
  value,
  onChange,
  placeholder = "Search...",
  className,
  inputClassName,
  "data-testid": testId,
  width = "w-[300px]",
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Only collapse if search is empty
        if (!value) {
          setIsExpanded(false);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isExpanded, value]);

  // Close on Escape key
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      if (!value) {
        setIsExpanded(false);
      }
    }
  };

  const handleClear = () => {
    onChange("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)} data-testid={testId}>
      {!isExpanded ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(true)}
          aria-label="Open search"
          data-testid={testId ? `${testId}-icon` : undefined}
        >
          <Search className="h-4 w-4" />
        </Button>
      ) : (
        <div className={cn("relative transition-all duration-200", width)}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300/70 pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              "pl-9 pr-8 bg-white/5 text-slate-100 ring-1 ring-white/10",
              inputClassName
            )}
            placeholder={placeholder}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
